import { useEffect, useState, useRef, ReactNode } from 'react'
import { HoverCard as HoverCardBase, HoverCardContent, HoverCardTrigger } from '@radix-ui/react-hover-card'

interface HoverCardProps {
  children: ReactNode
  trigger: ReactNode
  openDelay?: number
  closeDelay?: number
  align?: "center" | "end" | "start"
  sideOffset?: number
  side?: "top" | "right" | "bottom" | "left"
  className?: string
  triggerClassName?: string
  disabled?: boolean
}

export default function HoverCard({ 
  children, 
  trigger, 
  openDelay = 100, 
  closeDelay = 100, 
  align = 'center', 
  side = 'bottom',
  sideOffset = 4, 
  className, 
  triggerClassName, 
  disabled 
}: HoverCardProps) {
  const [isOpen, setIsOpen] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = (e: Event) => {
      const target = e.target as Node;

      if (contentRef.current && contentRef.current.contains(target)) {
        return; 
      }

      setIsOpen(false)
    }

    window.addEventListener('scroll', handleScroll, { capture: true })
    
    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true })
    }
  }, [isOpen])

  if (disabled) {
    return <div className={triggerClassName}>{trigger}</div>
  }

  return (
    <HoverCardBase open={isOpen} onOpenChange={setIsOpen} openDelay={openDelay} closeDelay={closeDelay}>
      <HoverCardTrigger asChild className={triggerClassName}>
        <div>{trigger}</div>
      </HoverCardTrigger>
      
      <HoverCardContent 
        ref={contentRef}
        align={align} 
        sideOffset={sideOffset} 
        side={side}
        className={className}
        hideWhenDetached={true}
      >
        {children}
      </HoverCardContent>
    </HoverCardBase>
  )
}